import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import controlCategoriesService, { ControlCategory } from '../../services/controlCategoriesService';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

const RBAC_ACTIVITY = 'CTR_MASTERS';

// workflow_state + audit fields come from the backend after the
// ctr_categories workflow migration.
type RowWithWorkflow = ControlCategory & { workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected' };

const ControlCategoriesTab: React.FC = () => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permLoading = viewPerm.loading;
  const [categories, setCategories] = useState<RowWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');

  const [selectedCategory, setSelectedCategory] = useState<RowWithWorkflow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<ControlCategory>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [isFieldEditMode, setIsFieldEditMode] = useState(false);
  const [workflowOpenFor, setWorkflowOpenFor] = useState<RowWithWorkflow | null>(null);
  const fieldVh = useFieldValidation();

  const loadCategories = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await controlCategoriesService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
      });
      const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
      setCategories(sorted as RowWithWorkflow[]);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load control categories' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, canView]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
    setIsFieldEditMode(false);
  }, [selectedCategory, showModal]);

  const handleRowClick = (category: RowWithWorkflow) => {
    setSelectedCategory(category);
    setFormData(category);
    fieldVh.reset();
    setIsEditMode(true);
    setIsFieldEditMode(false); // open read-only — "Edit" unlocks the fields
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (!canModify) return;
    setSelectedCategory(null);
    setFormData({ category_name: '', description: '' });
    fieldVh.reset();
    setIsEditMode(false);
    setShowModal(true);
  };

  const isApproved = selectedCategory?.workflow_state === 'approved';
  const fieldsDisabled = isEditMode && (!canModify || isApproved || !isFieldEditMode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    const name = (formData.category_name || '').trim();
    const valid = fieldVh.validateAll([
      { name: 'category_name', code: 109, value: name, required: true },
      { name: 'description', code: 110, value: formData.description || '' },
    ]);
    if (!valid) return;
    if (!isUniqueLocal(951, name, categories, selectedCategory?.id)) {
      fieldVh.setError('category_name', 'Category name already exists');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedCategory) {
        const { is_active, category_code, ...payload } = formData;
        await controlCategoriesService.update(selectedCategory.id, payload);
        setToast({ type: 'success', message: 'Control category updated successfully.' });
      } else {
        const { category_code, ...payload } = formData;
        await controlCategoriesService.create(payload);
        setToast({ type: 'success', message: 'Control category created successfully.' });
      }
      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      const msg = extractApiError(err, 'Operation failed.');
      // Code is auto-generated (CTR-N) — the only uniqueness clash this form can
      // raise is the name; anything else surfaces as a toast (never swallowed).
      if (/name/i.test(msg) && /exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError('category_name', msg);
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedCategory || !canModify || isApproved) return;
    const next = selectedCategory.is_active === 1 ? 0 : 1;
    const confirmFn = selectedCategory.is_active === 1 ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this category?' : 'Inactivate this category?',
      text: next
        ? 'It will appear in active lists again.'
        : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await controlCategoriesService.update(selectedCategory.id, { is_active: next });
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to update status.') });
    } finally {
      setToggling(false);
    }
  };

  // Workflow (submit/approve/reject/reopen) is driven end-to-end by the unified
  // <WorkflowModal> via the /api/v1/wf engine (kind="ctr_category").
  const closeWfModal = () => setWorkflowOpenFor(null);

  const totalRecords = categories.length;
  const paginatedRows = categories.slice((page - 1) * pageSize, page * pageSize);

  // ── Not-authorised gate ───────────────────────────────────────────────────
  if (!permLoading && !canView) {
    return (
      <div className="netbody-card flex flex-col h-full">
        <NoDataFound
          title="Not authorised"
          message={viewPerm.message || NOT_AUTHORISED_MSG}
        />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        <StatusSwitch
          value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next)}
          noun="control categories"
          testId="SEL_control_cat_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            data-testid="INP_control_cat_search"
            type="text"
            placeholder="Search categories…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
          <Button
            data-testid="BTN_control_cat_create"
            variant="fill"
            icon={Plus}
            onClick={handleAddNew}
            disabled={permLoading || !canModify}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
          >Create</Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Category Name</th>
                    <th className="table-list-th">Description</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', '3xs', '2xs', 'md', 'xs']} />}
                  {!loading && categories.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-list-cell">
                        <NoDataFound title="No Control Categories Found" />
                      </td>
                    </tr>
                  )}
                  {!loading && paginatedRows.map((category, idx) => (
                      <tr
                        data-testid={`BTN_control_cat_row_${category.id}_open`}
                        key={category.id}
                        className={`table-list-row${category.is_active === 1 ? '' : ' is-inactive'}`}
                        onClick={() => handleRowClick(category)}
                      >
                        <td
                          className="table-list-cell table-list-cell--dot"
                          onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(category); }}
                          title="Workflow & history"
                          data-testid={`BTN_control_cat_row_${category.id}_wf`}
                        >
                          <ApprovalDot
                            state={(category.workflow_state || 'draft') as any}
                            onClick={e => { e.stopPropagation(); setWorkflowOpenFor(category); }}
                            title="Workflow & history"
                          />
                        </td>
                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                        <td className="table-list-cell"><span className="cell-code">{category.code ?? '—'}</span></td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title cell-name-lock">
                            {category.workflow_state === 'approved' && (
                              <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                            )}
                            {category.category_name}
                          </span>
                        </td>
                        <td className="table-list-cell table-list-cell--truncate" title={category.description || ''}>
                          {category.description || <span className="text-gray-400 italic text-xs">No description</span>}
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="categories"
            />
          </div>
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
        <Modal.Header>
          {isEditMode && selectedCategory
            ? <>{isApproved ? 'Viewing (locked)' : (canModify && isFieldEditMode) ? 'Editing' : 'Viewing'} "<span className="item-highlight">{selectedCategory.category_name}</span>"</>
            : 'Create Control Category'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
            <div className="approved-banner">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form data-testid="MOD_control_cat_form" id="ctrl-category-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedCategory as any)?.code || '—'}
                disabled
                data-testid="INP_control_cat_code"
              />
            )}
            <ValidatedInput
              vh={fieldVh}
              name="category_name"
              code={109}
              required
              data-testid="INP_control_cat_name"
              label="Category Name"
              value={formData.category_name || ''}
              onChange={e => setFormData({ ...formData, category_name: e.target.value })}
              disabled={fieldsDisabled}
            />
            <ValidatedTextarea
              vh={fieldVh}
              name="description"
              code={110}
              data-testid="INP_control_cat_description"
              label="Description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {isEditMode && selectedCategory && canModify && !isApproved && (
            <Button
              data-testid="BTN_control_cat_toggle_active"
              variant={selectedCategory.is_active === 1 ? 'inactive-outline' : 'active-outline'}
              icon={selectedCategory.is_active === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={permLoading || toggling || submitting}
            >
              {toggling ? '...' : selectedCategory.is_active === 1 ? 'Inactive' : 'Active'}
            </Button>
          )}
          {!isEditMode && (
            <Button
              data-testid="BTN_control_cat_submit"
              type="submit"
              form="ctrl-category-form"
              variant="fill"
              icon={Save}
              disabled={permLoading || submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
          {isEditMode && canModify && !isApproved && (
            isFieldEditMode ? (
              <Button
                data-testid="BTN_control_cat_update"
                variant="update"
                icon={Check}
                onClick={handleSubmit}
                disabled={permLoading || submitting || toggling}
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            ) : (
              <Button
                data-testid="BTN_control_cat_edit"
                variant="fill"
                icon={Edit2}
                onClick={() => setIsFieldEditMode(true)}
                disabled={permLoading || toggling}
              >
                Edit
              </Button>
            )
          )}
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={closeWfModal}
        kind="ctr_category"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={loadCategories}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ControlCategoriesTab;
